import React, { memo } from "react";
import OtpInput from "react-otp-input";
import PropTypes from "prop-types";
import styled from "styled-components";
import { makeStyles } from "@material-ui/core/styles";

const ErrorMessage = styled.div`
  margin: 11px auto 0;
  width: ${({ $numInputs }) =>
    $numInputs ? `${$numInputs * 74 - 24}px` : "auto"};
  font-size: 16px;
  line-height: 24px;
  color: #d54040;
  word-break: keep-all;
  white-space: pre-wrap;
  font-family: "Inter";
`;

const useStyles = makeStyles(theme => ({
  otpContainer: {
    display: "flex",
    justifyContent: "center"
  },
  input: {
    margin: "0 12px",
    padding: "6px 0",
    width: "50px !important",
    fontSize: "28px",
    lineHeight: "41px",
    color: "#0A1F44",
    border: 0,
    outline: "none",
    borderBottom: "2px solid #C4CAE0",
    backgroundColor: "transparent",

    "&:focus": {
      outline: "none",
      borderBottomColor: "#003DA5"
    },

    [theme.breakpoints.down("sm")]: {
      margin: "0 8px",
      width: "32px !important",
      fontSize: "20px",
      lineHeight: "29px"
    }
  },
  emptyInput: {
    borderBottomColor: "#003DA5"
  },
  errorInput: {
    borderBottomColor: "#D54040 !important",

    "&:focus": {
      borderBottomColor: "#D54040 !important"
    }
  },
  errorMessage: {
    [theme.breakpoints.down("sm")]: {
      width: "auto !important"
    }
  }
}));

function CustomizedOtpInput({
                              value = "",
                              onChange,
                              numInputs = 6,
                              isDisabled = false,
                              errorMessage = "",
                              shouldAutoFocus = false,
                              className
                            }) {
  const classes = useStyles();
  const hasError = Boolean(errorMessage);

  return (
      <div className={className}>
        <OtpInput
            value={value}
            onChange={onChange}
            numInputs={numInputs}
            shouldAutoFocus={shouldAutoFocus}
            inputType="tel"
            containerStyle={classes.otpContainer}
            renderInput={(inputProps, index) => {
              const inputValue = value?.toString().charAt(index) ?? "";

              const inputClassName = [
                classes.input,
                inputValue === "" && classes.emptyInput,
                hasError && classes.errorInput
              ]
                  .filter(Boolean)
                  .join(" ");

              return (
                  <input
                      {...inputProps}
                      className={inputClassName}
                      disabled={isDisabled}
                      aria-invalid={hasError}
                      aria-describedby={hasError ? "otp-error-message" : undefined}
                  />
              );
            }}
        />

        {hasError && (
            <ErrorMessage
                id="otp-error-message"
                role="alert"
                aria-live="polite"
                className={`${classes.errorMessage} alert`}
                $numInputs={numInputs}
            >
              {errorMessage}
            </ErrorMessage>
        )}
      </div>
  );
}

CustomizedOtpInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  numInputs: PropTypes.number,
  isDisabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  shouldAutoFocus: PropTypes.bool,
  className: PropTypes.string
};

export default memo(CustomizedOtpInput);